import { ProgrammingHierarchical } from '@domain/builders';
import { LongDateTableItem } from '../../domain/entities';
import { ItemValues, TKeyItemValue } from '@domain/entities';
import { ILongDataTable } from '@domain/interfaces';
import { DateTimeAdapter } from '@domain/adapters';
import { toTitleCase } from '@domain/helpers';

type TUpdateTemporalValue = {
  readonly id: string;
  readonly key: TKeyItemValue;
  readonly value: number;
  readonly items: LongDateTableItem[];
};

export class LongDateTableData implements ILongDataTable {
  #cachedRows: LongDateTableItem[] | null = null;

  // eslint-disable-next-line no-empty-function
  constructor(readonly data: ProgrammingHierarchical[]) {}

  get hasData() {
    return !!this.data?.length;
  }

  listDates() {
    const data = new Set<string>();
    this.data?.forEach((curr) => {
      const date = curr.dateLabel();
      data.add(date);
    });

    return [...data];
  }

  calculateTotals(items: LongDateTableItem[]) {
    const mapData = new Map<string, ItemValues>();

    items
      .filter((dateItem) => dateItem.nestedLevel === 0)
      .forEach((dateItem) => {
        const entries = [...dateItem.values.entries()];

        entries.forEach(([key, value]) => {
          if (mapData.has(key)) {
            const prev = mapData.get(key);
            mapData.set(key, prev.addValues(value));
            return;
          }

          mapData.set(key, value);
        });
      });

    return Object.fromEntries(mapData.entries());
  }

  filterRows(items: LongDateTableItem[], segmentedZoneIds: string[]) {
    if (!segmentedZoneIds.length) {
      return items;
    }

    const selectedIds = new Set(segmentedZoneIds);
    const segmentedZoneByItemId = new Map<string, string>();

    items.forEach((item) => {
      if (item.nestedLevel === 0) {
        segmentedZoneByItemId.set(item.id, item.id);
        return;
      }

      if (item.nestedLevel === 1) {
        segmentedZoneByItemId.set(item.id, item.parentId);
        return;
      }

      segmentedZoneByItemId.set(
        item.id,
        segmentedZoneByItemId.get(item.parentId) || '',
      );
    });

    return items.filter((item) =>
      selectedIds.has(segmentedZoneByItemId.get(item.id)),
    );
  }

  dataRows() {
    if (this.#cachedRows) {
      return this.#cachedRows;
    }

    const mapTable = new Map<string, LongDateTableItem>();

    this.data.forEach((filial) => {
      filial.segmentedZones.forEach((sz) => {
        // Add segmented zones lvl 0
        if (mapTable.has(sz.segmentedZoneId)) {
          mapTable
            .get(sz.segmentedZoneId)
            .values.set(filial.dateLabel(), sz.totals());
        } else {
          mapTable.set(
            sz.segmentedZoneId,
            LongDateTableItem.fromJson({
              id: sz.segmentedZoneId,
              label: toTitleCase(sz.segmentedZoneName),
              hasToggle: true,
              values: new Map([[filial.dateLabel(), sz.totals()]]),
            }),
          );
        }

        sz.bags.forEach((bag) => {
          const keyBag = `${sz.segmentedZoneId}.${bag.bagId}`;
          // Add segmented bags lvl 1
          if (mapTable.has(keyBag)) {
            mapTable.get(keyBag).values.set(filial.dateLabel(), bag.totals());
          } else {
            mapTable.set(
              keyBag,
              LongDateTableItem.fromJson({
                id: keyBag,
                label: bag.label,
                nestedLevel: 1,
                parentId: sz.segmentedZoneId,
                hasToggle: true,
                values: new Map([[filial.dateLabel(), bag.totals()]]),
              }),
            );
          }

          bag.timeFrames.forEach((tframe) => {
            const keyTFrame = `${sz.segmentedZoneId}.${bag.bagId}.${tframe.timeFrameId}`;
            const canEdit = DateTimeAdapter.isGreatherThanToday(
              filial.dateLabel(),
            );
            const tfData = ItemValues.fromJson({
              available: tframe.data.available,
              scheduled: tframe.data.scheduledCount,
              initial: tframe.data.initialCapacity,
              canEdit,
            });

            // add timeframes lvl 2
            if (mapTable.has(keyTFrame)) {
              mapTable.get(keyTFrame).values.set(filial.dateLabel(), tfData);
            } else {
              mapTable.set(
                keyTFrame,
                LongDateTableItem.fromJson({
                  id: keyTFrame,
                  label: toTitleCase(tframe.timeFrameSlotName),
                  nestedLevel: 2,
                  parentId: keyBag,
                  values: new Map([[filial.dateLabel(), tfData]]),
                }),
              );
            }
          });
        });
      });
    });

    this.#cachedRows = [...mapTable.values()];
    return this.#cachedRows;
  }

  updateTemporalValue({ id, items, key, value }: TUpdateTemporalValue) {
    const [segmentedId, bagId, timeFrameId, date] = id.split('.');
    const bagKey = `${segmentedId}.${bagId}`;
    const timeFrameKey = `${segmentedId}.${bagId}.${timeFrameId}`;

    const mapItems = new Map<string, LongDateTableItem>(
      items.reduce((prev, item) => [...prev, [item.id, item]], []),
    );

    const newTimeFrame = mapItems
      .get(timeFrameKey)
      .updateValue(date, key, value);

    const bagValue = items
      .filter((item) => item.parentId === bagKey)
      .reduce((prev, item) => {
        if (item.id === newTimeFrame.id) {
          return prev + newTimeFrame.getValue(date, key);
        }

        return prev + item.getValue(date, key);
      }, 0);

    const newBag = mapItems.get(bagKey).updateValue(date, key, bagValue);

    const segmenetdZoneValue = items
      .filter((item) => item.parentId === segmentedId)
      .reduce((prev, item) => {
        if (item.id === newBag.id) {
          return prev + newBag.getValue(date, key);
        }

        return prev + item.getValue(date, key);
      }, 0);

    const newSegmentedZone = mapItems
      .get(segmentedId)
      .updateValue(date, key, segmenetdZoneValue);

    return items.map((item) => {
      if (item.id === newTimeFrame.id) {
        return newTimeFrame;
      }

      if (item.id === newBag.id) {
        return newBag;
      }

      if (item.id === newSegmentedZone.id) {
        return newSegmentedZone;
      }

      return item;
    });
  }
}
